import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getAttachmentBindingsOptions,
  getMicroAppQueryKey,
  listMicroAppsQueryKey,
  type MicroAppPublish,
  type MicroAppSourceModeOption,
  publishMicroAppMutation,
  republishMicroAppMutation,
  updateMicroAppMutation,
} from "@/api";
import type { DraftView } from "../draftModel";
import { useDraftRun } from "../useDraftRun";

/**
 * Publish (a chat's HTML artifact becomes an app, linked to its chat) or Republish (an existing app takes the
 * new page, then the draft's name and description, if it has any). Either way, a page with live data first asks
 * whose access serves each source: the page, and the app it goes to, are the model's choice.
 */
export function useMicroAppDraft(draft: DraftView) {
  const { t } = useTranslation("buildChat", { keyPrefix: "microApp" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const [choosing, setChoosing] = useState<MicroAppSourceModeOption[] | null>(null);
  const overrides = useRef<Record<string, string>>({});
  const publish = useMutation(publishMicroAppMutation());
  const republish = useMutation(republishMicroAppMutation());
  const update = useMutation(updateMicroAppMutation());
  const appUuid = useRef<string | null>(draft.targetUuid);
  const body = draft.draft as MicroAppPublish;
  const details = {
    ...(body.name ? { name: body.name } : {}),
    ...(body.description ? { description: body.description } : {}),
  };
  const path = { app_uuid: draft.targetUuid ?? "" };
  const chosenModes = () => (Object.keys(overrides.current).length > 0 ? { authModeOverrides: overrides.current } : {});

  const steps = draft.targetUuid
    ? [
        {
          key: "republish",
          label: t("republishStep"),
          run: async () => {
            const page = { attachmentUuid: body.attachmentUuid, ...chosenModes() };
            await republish.mutateAsync({ path, body: page, throwOnError: true });
          },
        },
        {
          key: "details",
          label: t("detailsStep"),
          skip: Object.keys(details).length === 0,
          run: async () => {
            await update.mutateAsync({ path, body: details, throwOnError: true });
          },
        },
      ]
    : [
        {
          key: "publish",
          label: t("publishStep"),
          run: async () => {
            const page = { attachmentUuid: body.attachmentUuid, ...details, ...chosenModes() };
            const result = await publish.mutateAsync({ body: page, throwOnError: true });
            appUuid.current = result.data.uuid;
          },
        },
      ];
  const run = useDraftRun(draft.draftId, steps, {
    fallback: t("failed"),
    success: draft.targetUuid ? t("republished") : t("published"),
    entityUuid: () => appUuid.current,
    invalidate: (uuid) => [listMicroAppsQueryKey(), getMicroAppQueryKey({ path: { app_uuid: uuid } })],
  });
  const startPublish = async () => {
    try {
      const options = getAttachmentBindingsOptions({ path: { attachment_uuid: body.attachmentUuid } });
      const { data } = await queryClient.fetchQuery(options);
      if (data.isLive && data.sources.length > 0) setChoosing(data.sources);
      else run.run();
    } catch {
      // Not knowing which sources would use the publisher's access, don't publish.
      toast.error(t("access.checkFailed"));
    }
  };
  return {
    body,
    ...run,
    run: () => void startPublish(),
    choosing,
    cancelChoice: () => setChoosing(null),
    publishWith: (chosen: Record<string, string>) => {
      overrides.current = chosen;
      setChoosing(null);
      run.run();
    },
  };
}
